import { 
  Box, 
  Heading, 
  Flex, 
  Text, 
  VStack, 
  HStack, 
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import RecentEmployeeTable from "../../components/tables/RecentEmployeeTable";
import { FormatDate, getTodaysDate, numericFormat } from "../../utils";
import { useRecentEntries } from "../../services/reportService";
import InputComponent from "../../components/inputs";
import { FiCalendar, FiClock, FiMapPin, FiUsers } from "react-icons/fi";

const RecentEntries = () => {
  const today = useMemo(() => {
    const date = new Date();
    const day = date.getDate();
    const mnt = date.getMonth() + 1;
    const yr = date.getFullYear();
    return `${yr}-0${mnt}-${day}`;
  }, []);

  const [query, setQuery] = useState({ date: getTodaysDate() });
  const { recentEntriesData, recentEntriesIsLoading, recentEntriesFilter } = useRecentEntries({ enabled: true });
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Calculate entry statistics
  const entryStats = useMemo(() => {
    if (!recentEntriesData?.length) return { total: 0, checkIn: 0, checkOut: 0, uniqueEmployees: 0 };
    
    const uniqueEmployees = new Set();
    let checkIn = 0;
    let checkOut = 0;
    
    recentEntriesData.forEach(entry => {
      const name = entry.message?.split(' ')[0] || 'Unknown';
      uniqueEmployees.add(name);
      
      if (entry.message?.includes('check-in') || entry.message?.includes('Check-in')) {
        checkIn++;
      } else if (entry.message?.includes('check-out') || entry.message?.includes('Check-out')) {
        checkOut++;
      }
    });
    
    return {
      total: recentEntriesData.length,
      checkIn,
      checkOut,
      uniqueEmployees: uniqueEmployees.size
    };
  }, [recentEntriesData]);

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch" mb={8}>
        <Heading size="lg" color="gray.900" fontWeight="600">
          Recent Entries
        </Heading>
        <Text color="gray.600" fontSize="md">
          Track employee attendance and entry activities
        </Text>
      </VStack>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <StatCard
          label="Total Entries"
          value={entryStats.total}
          icon={<FiClock />}
          color="blue"
        />
        <StatCard
          label="Check-ins"
          value={entryStats.checkIn}
          icon={<FiUsers />}
          color="green"
        />
        <StatCard
          label="Check-outs"
          value={entryStats.checkOut}
          icon={<FiUsers />}
          color="orange"
        />
        <StatCard
          label="Active Employees"
          value={entryStats.uniqueEmployees}
          icon={<FiUsers />}
          color="purple"
        />
      </SimpleGrid>

      {/* Date Filter Section */}
      <Box
        bg={bgColor}
        borderRadius="16px"
        p={6}
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        mb={6}
      >
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
          <VStack align="start" spacing={2}>
            <Heading size="md" color="gray.900" fontWeight="600">
              Activities for {query.date}
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {entryStats.total} entries found for this date
            </Text>
          </VStack>
          <HStack spacing={4} align="center">
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Select Date:
            </Text>
            <InputComponent
              borderRadius="12px"
              containerColor="white"
              id="date"
              isRequired={true}
              placeholder=""
              value={query.date}
              type="date"
              handleChange={(e) => {
                const d = numericFormat(e.target.value);
                setQuery({ date: FormatDate(e.target.value, true) });
                recentEntriesFilter(d);
              }}
              max={today}
              bg="gray.50"
              borderColor="gray.200"
              _focus={{ borderColor: "blue.500", bg: "white" }}
              _hover={{ bg: "gray.100" }}
            />
          </HStack>
        </Flex>
      </Box>

      {/* Entries Table */}
      <Box
        bg={bgColor}
        borderRadius="16px"
        shadow="sm"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <RecentEmployeeTable 
          employeeData={recentEntriesData} 
          isLoading={recentEntriesIsLoading}
        />
      </Box>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", iconBg: "blue.100" },
    green: { bg: "green.50", color: "green.600", iconBg: "green.100" },
    red: { bg: "red.50", color: "red.600", iconBg: "red.100" },
    orange: { bg: "orange.50", color: "orange.600", iconBg: "orange.100" },
    purple: { bg: "purple.50", color: "purple.600", iconBg: "purple.100" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.100"
      _hover={{ transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg={colors.iconBg}
          color={colors.color}
        >
          {icon}
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {label}
          </Text>
          <Text fontSize="2xl" fontWeight="700" color="gray.900">
            {value}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export default RecentEntries;
